import { useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { NIRValidationContext, validateNIRComplete } from '@Common/validators/nirValidation';

/**
 * Custom validator for NIR fields in BO forms
 * Provides real-time validation with access to other form fields
 */
export function useNIRValidator() {
  const translate = useTranslate();
  const { getValues } = useFormContext();

  return (nir: string | null | undefined) => {
    if (!nir) {
      return undefined; // NIR is optional, handled by required() validator
    }

    const formValues = getValues();

    const context: NIRValidationContext = {
      nir,
      gender: formValues.gender || null,
      date_of_birth: formValues.date_of_birth ? new Date(formValues.date_of_birth) : null,
      postcode_of_birth: formValues.postcode_of_birth || null,
    };

    const validation = validateNIRComplete(context);

    if (!validation.isValid && validation.errors.length > 0) {
      // Return the first error translated
      const [firstError] = validation.errors;
      return translate(`resources.bo_common.b2b.employees.validation.nir.${firstError}`);
    }

    return undefined;
  };
}

/**
 * Simple NIR validator that only checks format
 * For use when other form fields are not available
 */
export function createSimpleNIRValidator(translate: (key: string) => string) {
  return (nir: string | null | undefined) => {
    if (!nir) {
      return undefined;
    }

    const validation = validateNIRComplete({ nir });

    if (!validation.isValid && validation.errors.length > 0) {
      const [firstError] = validation.errors;
      return translate(`resources.bo_common.b2b.employees.validation.nir.${firstError}`);
    }

    return undefined;
  };
}
